import { Loader2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { CardTitle } from '@/components/ui/card'

interface FilterDefinition {
  name: string
  description: string
  type: string
  default: boolean
}

export interface FilterPanelProps {
  show: boolean
  filters: Record<string, boolean>
  availableFilters: Array<FilterDefinition> | undefined
  filtersLoading: boolean
  onClose: () => void
  onFilterChange: (name: string, checked: boolean) => void
}

export function FilterPanel({
  show,
  filters,
  availableFilters,
  filtersLoading,
  onClose,
  onFilterChange,
}: FilterPanelProps) {
  const { t } = useTranslation()

  if (!show) return null

  return (
    <div className="absolute inset-0 z-[1001] bg-black/50 backdrop-blur-sm">
      <div
        className="absolute inset-x-0 bottom-0 z-[1002] max-h-[60vh] flex flex-col rounded-t-2xl bg-background shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label={t('search.filtersTitle')}
      >
        <div className="flex items-center justify-between border-b border-border bg-background p-4 shrink-0">
          <CardTitle className="text-base">
            {t('search.filtersTitle')}
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close filters"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {filtersLoading && (
            <div className="text-center text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 mx-auto mb-2 animate-spin" />
              {t('common.loading')}...
            </div>
          )}
          {!filtersLoading && availableFilters && (
            <>
              {availableFilters.map((filter) => {
                const filterId = `filter-${filter.name}`
                return (
                  <div
                    key={filter.name}
                    className="flex items-center justify-between"
                  >
                    <div className="flex-1 pe-4">
                      <label htmlFor={filterId} className="text-sm font-medium">
                        {filter.name
                          .split('_')
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1),
                          )
                          .join(' ')}
                      </label>
                      <p className="text-xs text-muted-foreground">
                        {filter.description}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        <Badge
                          variant="outline"
                          className="text-xs px-1.5 py-0"
                        >
                          {filter.type}
                        </Badge>
                      </p>
                    </div>
                    <Switch
                      id={filterId}
                      checked={filters[filter.name] ?? filter.default}
                      onCheckedChange={(checked) =>
                        onFilterChange(filter.name, checked)
                      }
                    />
                  </div>
                )
              })}
            </>
          )}
          {!filtersLoading &&
            (!availableFilters || availableFilters.length === 0) && (
              <div className="text-center text-muted-foreground py-4">
                {t('search.noFiltersAvailable') || 'No filters available'}
              </div>
            )}
        </div>
      </div>
    </div>
  )
}
